import { Dispatch, useEffect, useRef, useState } from 'react';
import { FacephiSelphiWidget } from '@facephi/sdk-web-react';
import { useDeviceSize } from '@facephi/ui-react';
import AgoraRTC from 'facephi-agora-rtc-react';
import { useTranslation } from 'react-i18next';
import { useData } from '../../providers';
import { ActionRequest } from '../../reducers';
import { SELPHI_SDK, SELPHI_WIDGET, StatusOperations } from '../../states';
import { StatusTypeProps } from '../results';
import { ValidatingIdentity } from '../validating';
import { ValidStatus } from '../validStatus';
import { SlefieSignHeader } from './SelfieSignHeader';
import {
  DeniedActions,
  DeniedButton,
  SelfieSignResult,
  SelfieSignWrapper,
  WidgetWrapper,
} from './Styles';
import { TimeoutStatus } from './TimeoutStatus';

type Props = {
  onCancel(state: boolean, documentSigned?: StatusTypeProps): void;
  onSign(bestImage: string): Promise<void>;
  onClose(): void;
  showSignProcess: boolean;
  signatureAttemps: number;
  showSelphi: boolean;
  setShowSelphi: Dispatch<boolean>;
};

const SelfieSign = ({
  onCancel,
  showSignProcess,
  onClose,
  onSign,
  signatureAttemps,
  showSelphi,
  setShowSelphi,
}: Props) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selphiRef = useRef<any>();
  const { i18n, t } = useTranslation();
  const { documentSignatureState, dispatchDocumentSignature } = useData();
  const [showHeader, setShowHeader] = useState<boolean>(false);
  const { isMobile } = useDeviceSize();

  const targetNode = document.querySelector('div[class*="SelfieSignWrapper"]');
  useEffect(() => {
    const config = { attributes: false, childList: true, subtree: true };
    const callback = (mutationList: MutationRecord[]) => {
      for (const mutation of mutationList) {
        setShowHeader(
          mutation.addedNodes[0]?.nodeName === SELPHI_SDK ||
            mutation.nextSibling?.nodeName === SELPHI_SDK ||
            mutation.addedNodes[0]?.nodeName === SELPHI_WIDGET ||
            mutation.nextSibling?.nodeName === SELPHI_WIDGET
        );
      }
    };

    const observer = new MutationObserver(callback);

    targetNode && observer.observe(targetNode, config);

    return () => {
      setShowHeader(false);
      observer.disconnect();
    };
  }, [targetNode]);

  useEffect(() => {
    if (!documentSignatureState.error) {
      configTrack();
    } else {
      setShowSelphi(false);
    }
  }, [documentSignatureState.error]);

  useEffect(() => {
    setShowSelphi(showSignProcess);
  }, [showSignProcess]);

  const configTrack = async () => {
    try {
      const track = await AgoraRTC.createCameraVideoTrack({
        facingMode: 'user',
        encoderConfig: '720p_6',
      });
      track.stop();
    } finally {
      setShowSelphi(true);
    }
  };

  const onExtractionTimeout = () => {
    dispatchDocumentSignature({
      type: ActionRequest.SET_DATA,
      payload: { signed: null },
    });
    setShowSelphi(false);
  };

  const onUserCancel = () => {
    dispatchDocumentSignature({
      type: ActionRequest.SET_ERROR,
    });
  };

  const onUserRetry = () => {
    dispatchDocumentSignature({
      type: ActionRequest.RESET,
    });
    setShowSelphi(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onExtractionFinish = async (event: any) => {
    onSign(event.detail?.detail?.bestImage?.src);
  };

  useEffect(() => {
    const selphi = selphiRef.current;
    if (selphi) {
      selphi.addEventListener('extractionFinish', onExtractionFinish);
      selphi.addEventListener('userCancel', onUserCancel);
      selphi.addEventListener('extractionTimeout', onExtractionTimeout);
      selphi.addEventListener('errorTimeout', onExtractionTimeout);
      selphi.addEventListener('timeoutButtonClick', onExtractionTimeout);
    }

    return () => {
      if (selphi) {
        selphi.removeEventListener('extractionFinish', onExtractionFinish);
        selphi.removeEventListener('userCancel', onUserCancel);
        selphi.removeEventListener('extractionTimeout', onExtractionTimeout);
        selphi.removeEventListener('errorTimeout', onExtractionTimeout);
        selphi.removeEventListener('timeoutButtonClick', onExtractionTimeout);
      }
    };
  }, [selphiRef]);

  const showResults =
    !documentSignatureState.loading &&
    !showSelphi &&
    (documentSignatureState.status === StatusOperations.DENIED ||
      documentSignatureState.status === StatusOperations.SUCCEEDED ||
      documentSignatureState.status === StatusOperations.CONNECTION_ERROR);

  const validStatusMessage =
    documentSignatureState.status === StatusOperations.CONNECTION_ERROR ||
    documentSignatureState.status === StatusOperations.DENIED
      ? 'The document could not be signed'
      : 'You have signed the document correctly';

  const isTimeout =
    documentSignatureState.status === StatusOperations.DENIED &&
    documentSignatureState.data?.signed === null;

  return (
    <SelfieSignWrapper
      flexDirection="column"
      alignItems="center"
      justifyContent={isMobile ? 'space-between' : 'center'}
    >
      {showResults ? (
        <>
          <SlefieSignHeader
            facephiLogo={!isMobile}
            readyToShow={true}
            onClose={onClose}
          />
          <SelfieSignResult flexDirection="column" alignItems="center">
            {isTimeout ? (
              <TimeoutStatus />
            ) : (
              <ValidStatus
                state={
                  documentSignatureState.status ===
                    StatusOperations.CONNECTION_ERROR ||
                  documentSignatureState.status === StatusOperations.DENIED
                    ? 'denied'
                    : 'success'
                }
                message={t(validStatusMessage) || undefined}
                idMessage="sign_result_title_text"
                idLottie="sign_result_lottie"
              />
            )}
            {(documentSignatureState.status === StatusOperations.DENIED ||
              documentSignatureState.status ===
                StatusOperations.CONNECTION_ERROR) &&
              signatureAttemps < 3 && (
                <DeniedActions justifyContent="center" $isTimeout={isTimeout}>
                  {!isTimeout && (
                    <DeniedButton
                      variant="outline"
                      onClick={() =>
                        onCancel(false, documentSignatureState.status)
                      }
                      testId="sign_result_cancel_button"
                    >
                      {t('Cancel')}
                    </DeniedButton>
                  )}
                  <DeniedButton
                    onClick={onUserRetry}
                    $isTimeout={isTimeout}
                    testId="sign_result_retry_button"
                  >
                    {t('Try again')}
                  </DeniedButton>
                </DeniedActions>
              )}
          </SelfieSignResult>
        </>
      ) : documentSignatureState.loading ? (
        <ValidatingIdentity />
      ) : showSelphi ? (
        <WidgetWrapper>
          <SlefieSignHeader
            infoIcon
            facephiLogo
            readyToShow={showHeader}
            onClose={onClose}
          />
          <FacephiSelphiWidget
            ref={selphiRef}
            bundlePath={`${process.env.PUBLIC_URL}/widgets/selphi`}
            className="sdk-widget selphi-widget"
            language={i18n.language.split('-')[0]}
            tutorial={false}
            interactible={false}
            cropFactor={1.7}
            stabilizationStage={true}
            previewCapture={false}
            advancedTracking={false}
          />
        </WidgetWrapper>
      ) : null}
    </SelfieSignWrapper>
  );
};

export default SelfieSign;
